import { LowerCasePipe, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { Component, computed, effect, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppInfo } from '../../../../../../global/environment';
import { PostCode } from '../../../../../../global/postcode';
import { ButtonComponent } from '../../../../../../shared/components/button/button.component';
import { RadioButtonComponent } from '../../../../../../shared/components/forms/radio-button/radio-button.component';
import { TelephoneLinkComponent } from '../../../../../../shared/components/telephone-link/telephone-link.component';
import { formatAddress } from '../../../../../../shared/utils/formatting';
import { MmrService } from '../../../../services/mmr.service';
import { ClaimDetailsPanelCreateBooking } from '../../../../types/mmr-claims.type';

type YesNo = 'Yes' | 'No';

const BAXI_PHONE_NUMBER = '0344 335 6556';

@Component({
  selector: 'app-create-booking',
  imports: [
    FormsModule,
    RadioButtonComponent,
    ButtonComponent,
    LowerCasePipe,
    SlicePipe,
    TelephoneLinkComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './create-booking.component.html',
  styleUrl: './create-booking.component.scss',
})
export class CreateBookingComponent {
  readonly isWhiteLabel = AppInfo.Environment.showPoweredByLogo;

  claimType = input.required<'repair' | 'service'>();
  onDownloadPlanDocs = input.required<() => void>();
  productType = signal<string>('');

  claimTypeMapping: Record<string, { image: string; label: string }> = {
    BD: {
      image: 'bd',
      label: 'It broke down',
    },
    AD: {
      image: 'ad',
      label: this.isWhiteLabel ? 'It was damaged by accident' : 'It got damaged by accident',
    },
  };
  currentStep = signal(1);

  selectedGasProduct = signal(false);

  selectedClaimTypeCode = signal<string | undefined>(undefined);

  protected viewModel = computed(() => {
    const { claims } = this.mmrService.planClaimsDetails;
    const claimValue = claims.value()?.[this.claimType()] as ClaimDetailsPanelCreateBooking;
    const claimTypes = (claimValue?.claimTypes ?? [])
      .map((i) => {
        if (i.claimTypeCode === 'BD') return 'breakdowns';
        if (i.claimTypeCode === 'AD') return 'accidental damage';
        return undefined;
      })
      .filter((type): type is 'breakdowns' | 'accidental damage' => type !== undefined)
      .sort((a, b) => {
        if (a === 'breakdowns') return -1;
        if (b === 'breakdowns') return 1;
        return 0;
      });

    const addressInformation = claims.value()?.addressInformation;

    if (addressInformation) {
      delete addressInformation['countryCode'];
    }

    const hasServiceOption = !!claims.value()?.service;
    const dataLists = claimValue?.dataLists;
    const isService = this.claimType() === 'service';
    const isGasApplianceService = isService && (claims.value()?.planOverview?.isGasAppliance ?? false);
    const isBaxi = claims.value()?.planOverview?.isBaxi ?? false;
    const stepsToFlowThrough = this.calculateStepsToAsk(claimValue, this.selectedGasProduct(), {
      isGasApplianceService,
      isBaxi,
    });
    const isAD = (claimValue?.claimTypes ?? []).some((i) => i.claimTypeCode === 'AD');
    const isBD = (claimValue?.claimTypes ?? []).some((i) => i.claimTypeCode === 'BD');
    const claimOptions = (claimValue?.claimTypes ?? []).filter((value) => {
      if (!isService && !this.claimTypeMapping[value.claimTypeCode]) {
        return false;
      }

      // Leave this hard written, don't "Optimise" as the isCurrentAvailable flag will only ever exists as false or undefined
      if (value.claimTypeCode === 'AD' && value.isCurrentlyAvailable === false) {
        return false;
      }

      return true;
    });
    const selectedClaimOption = claimOptions.find((option) => option.claimTypeCode === this.selectedClaimTypeCode());

    return {
      claims,
      isAD,
      isBD,
      hasServiceOption,
      product: claims.value()?.itemDetails?.itemDescription?.toLowerCase(),
      productType: this.productType(),
      claimTypesMessage: claimTypes?.join(' and '),
      claimOptions,
      selectedClaimOption,
      manufacturer: claims.value().itemDetails?.manufacturer,
      phoneNumber: claimValue?.planInWarrantyContactTel,
      postCode: addressInformation?.postalCode,
      claimType: this.claimType(),
      stepsToFlowThrough,
      dataLists,
      itemDescription: claims.value()?.itemDetails?.itemDescription,
      address: formatAddress(Object.values(addressInformation ?? '{}')),
      yourDetailsPageLink: `${AppInfo.Environment.myAccountURL}/my-account/my-details`,
      gasProduct: this.selectedGasProduct(),
      planInWarrantyPeriod: claims.value()?.planOverview.inWarranty,
      isTV: claims.value()?.planOverview.isTV,
      isGasApplianceService,
      isBaxi,
      baxiPhoneNumber: BAXI_PHONE_NUMBER,
    };
  });

  selectedProductType: string | undefined;
  gasSafetyAnswer: YesNo | undefined;
  gasSafetyType: string | undefined;
  addressCorrect: 'yes' | 'no' | undefined;
  serviceGasLeakAnswer: YesNo | undefined;
  boilerConditionAnswer: YesNo | undefined;
  baxiLetterAnswer: YesNo | undefined;

  callCostMessaging = signal<string | undefined>(undefined);

  get vm() {
    return this.viewModel();
  }

  get productTypeQuestionTitle(): string {
    return this.vm.isTV
      ? 'What size is your TV screen?'
      : `What type of ${this.vm.itemDescription.toLowerCase()} do you have?`;
  }

  constructor(private readonly mmrService: MmrService) {
    effect(() => {
      const claims = this.mmrService.planClaimsDetails.claims.value();
      const isBaxiGasService =
        this.claimType() === 'service' &&
        (claims?.planOverview?.isGasAppliance ?? false) &&
        (claims?.planOverview?.isBaxi ?? false);
      const postCode = claims?.addressInformation?.postalCode;
      if (isBaxiGasService && postCode && !this.callCostMessaging()) {
        PostCode.getCallCostMessagingForPostCode(postCode).subscribe({
          next: (messaging) => this.callCostMessaging.set(messaging),
        });
      }
    });
  }

  productTypeSelected(product: string): void {
    if (product !== this.selectedProductType) return;
    this.productType.set(product);
    const isGasProduct = product.toLowerCase().includes('gas');
    this.selectedGasProduct.set(isGasProduct);

    this.addressCorrect = undefined;
    if (!isGasProduct) {
      this.gasSafetyType = undefined;
      this.gasSafetyAnswer = undefined;
    }

    this.advancePastStep('productType');
  }

  moveOnFromGasSafety() {
    this.gasSafetyType = undefined;
    this.advancePastStep('gasSafety');
  }

  moveBackToGasASafety() {
    this.addressCorrect = undefined;
    this.goToStep('gasSafety');
  }

  claimTypeSelected(value: string, currentStepName: string): void {
    this.selectedClaimTypeCode.set(value);

    if (value === 'BD') {
      if (this.vm.planInWarrantyPeriod) {
        this.currentStep.set(1);
        this.gasSafetyType = undefined;
        this.gasSafetyAnswer = undefined;
        this.addressCorrect = undefined;
        this.selectedProductType = undefined;
        return;
      }
    }

    if (currentStepName === 'claimType') {
      this.currentStep.set(this.currentStep() + 1);
    }
  }

  onServiceGasLeakChange(): void {
    this.boilerConditionAnswer = undefined;
    this.baxiLetterAnswer = undefined;
    this.addressCorrect = undefined;
    if (this.serviceGasLeakAnswer === 'No') {
      this.advancePastStep('serviceGasLeak');
    } else {
      this.goToStep('serviceGasLeak');
    }
  }

  onBoilerConditionChange(): void {
    this.baxiLetterAnswer = undefined;
    this.addressCorrect = undefined;
    if (this.boilerConditionAnswer === 'Yes') {
      this.advancePastStep('boilerCondition');
    } else {
      this.goToStep('boilerCondition');
    }
  }

  onBaxiLetterChange(): void {
    this.addressCorrect = undefined;
    if (this.baxiLetterAnswer === 'Yes') {
      this.advancePastStep('baxiLetter');
    } else {
      this.goToStep('baxiLetter');
    }
  }

  private goToStep(stepName: string): void {
    this.currentStep.set(this.vm.stepsToFlowThrough.indexOf(stepName) + 1);
  }

  private advancePastStep(stepName: string): void {
    this.currentStep.set(this.vm.stepsToFlowThrough.indexOf(stepName) + 2);
  }

  calculateStepsToAsk(
    claimValue: ClaimDetailsPanelCreateBooking,
    selectedGasProduct: boolean,
    serviceFlow: { isGasApplianceService: boolean; isBaxi: boolean }
  ) {
    let steps = ['claimType'];

    if (claimValue?.questionsToAsk.manufacturer) {
      steps.push('manufacturer');
    }

    if (claimValue?.questionsToAsk.productType) {
      steps.push('productType');
    }

    if (serviceFlow.isGasApplianceService) {
      steps.push('serviceGasLeak', 'boilerCondition');
      if (serviceFlow.isBaxi) {
        steps.push('baxiLetter');
      }
    } else if (claimValue?.questionsToAsk.gasSafety || selectedGasProduct) {
      steps.push('gasSafety');
    }

    steps.push('address');

    return steps;
  }

  formatOption(option: string): string {
    if (this.vm.isTV) {
      const formattedOption = option.replace(/&/g, 'and');
      return formattedOption.replace(/^\s*Television\s-\s|\(\w*\)\s*/g, '').trim();
    }
    return option;
  }

  sortScreenSizes(arr: string[]): string[] {
    return arr.sort((a, b) => {
      const numA = parseInt(a.match(/(\d+)\s*"/)?.[1] ?? '0', 10);
      const numB = parseInt(b.match(/(\d+)\s*"/)?.[1] ?? '0', 10);
      return numA - numB;
    });
  }

  createBooking() {
    const claimValue = this.vm.selectedClaimOption;
    if (!claimValue) {
      return;
    }
    this.mmrService.navigateToStartClaim(this.claimType(), claimValue, this.vm.hasServiceOption, this.vm.productType);
  }
}
